// Third-party
import React from 'react';
import classNames from 'classnames';
import camelCase from 'lodash/camelCase';

// Components
import { SelectableButton } from '../buttons';
import DatePicker from '../inputs/DatePicker';

// Utils
import { formatMessage, FormattedMessage, formatLocalizedString } from '../../../utils/i18n';
import { OTHER_QUESTION_ITEM } from '@/utils/questionnaire';
import { convertDateToString } from '@/utils/dates';
import QUESTION_TYPES from '@/types/question-types';
import QuestionItem from '@/types/question-item';
import Locale from '@/types/locale';
import QuestionOptionItem from '@/types/question-option-item';
import SavedSections from '@/types/saved-sections';
import DOMPurify from 'dompurify';

type Props = {
  question: QuestionItem;
  questionId: string;
  showError?: boolean;
  locale: Locale;
  sectionId: string;
  saveAnswers: (sectionId: string, questionId: string, option: QuestionOptionItem, answer?: string) => void;
  clearAnswers?: (sectionId: string, questionId: string) => void;
  saveNotes?: (sectionId: string, questionId: string, notes?: string) => void;
  clearNotes?: (sectionId: string, questionId: string) => void;
  savedSections?: SavedSections;
};

type State = {
  textAnswer: string;
  textAnswerFocused: boolean;
  notes: string;
};
class Question extends React.Component<Props, State> {
  componentDidUpdate(prevProps) {
    if (this.props.questionId !== prevProps.questionId) {
      this.setState({ textAnswer: this.getTextAnswer() });
    }
  }

  checkOptionExists = (sectionId: string, questionId: string, option: string): boolean => {
    const { savedSections } = this.props;

    return (
      !!savedSections &&
      sectionId in savedSections &&
      questionId in savedSections[sectionId].questions &&
      savedSections[sectionId].questions[questionId].options.includes(option)
    );
  };

  otherIsSelected = () =>
    this.props.question.type === QUESTION_TYPES.MULTIPLE_CHOICES_WITH_OTHER &&
    this.checkOptionExists(this.props.sectionId, this.props.questionId, OTHER_QUESTION_ITEM._id);

  renderOptions = (options: QuestionOptionItem[], type?: QUESTION_TYPES) => {
    const { locale, question, questionId } = this.props;

    return options.map(option => {
      const active = this.checkOptionExists(this.props.sectionId, this.props.questionId, option._id);

      return (
        <label
          key={option._id}
          className={classNames(active && 'active', 'answer-button selectable button', `${question.disabled ? 'disabled' : 'selectable'}`)}
          aria-disabled={question.disabled ? true : undefined}
        >
          <input
            type={type === QUESTION_TYPES.MULTIPLE_CHOICES_MULTI_SELECT ? 'checkbox' : 'radio'}
            name={questionId}
            checked={active}
            onChange={() => this.onClickOption(option)}
            className="visually-hidden"
            aria-labelledby={`label-${option._id}`}
            {...(question.disabled ? { disabled: true, 'aria-disabled': true } : {})}
          />
          <span id={`label-${option._id}`}>{formatLocalizedString(option.description, locale)}</span>
        </label>
      );
    });
  };

  saveOrClearNotes = () => {
    if (this.state.notes !== '' && this.props.saveNotes) {
      this.props.saveNotes(this.props.sectionId, this.props.questionId, this.state.notes);
    } else if (this.props.clearNotes) {
      this.props.clearNotes(this.props.sectionId, this.props.questionId);
    }
  };

  saveOrClearAnswer = () => {
    if (this.state.textAnswer !== '') {
      this.props.saveAnswers(this.props.sectionId, this.props.questionId, OTHER_QUESTION_ITEM, this.state.textAnswer);
    } else if (this.props.clearAnswers) {
      this.props.clearAnswers(this.props.sectionId, this.props.questionId);
    }
  };

  onBlur = () => {
    this.saveOrClearAnswer();
    this.setState({ textAnswerFocused: false });
  };

  onNotesBlur = () => {
    this.saveOrClearNotes();
    this.setState({ textAnswerFocused: false });
  };

  onFocus = () => {
    this.saveOrClearAnswer();
    this.setState({ textAnswerFocused: true });
  };

  onTextAnswerChange = (event: React.FormEvent<HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (value !== this.state.textAnswer) {
      this.setState({ textAnswer: value }, () => {
        this.saveOrClearAnswer();
      });
    }
  };

  onDateChange = (date: Date) => {
    const dateString = convertDateToString(date) ?? '';

    this.setState({ textAnswer: dateString }, () => {
      this.saveOrClearAnswer();
    });
  };

  onClickOption = (option: QuestionOptionItem) => {
    if (this.state.textAnswerFocused) {
      this.setState({ textAnswerFocused: false });
    }
    this.props.saveAnswers(this.props.sectionId, this.props.questionId, option);
  };

  getTextAnswer = () => {
    let text;
    if (this.props.question.type !== QUESTION_TYPES.MULTIPLE_CHOICES && this.props.savedSections !== undefined) {
      const savedSection = this.props.savedSections[this.props.sectionId]?.questions[this.props.questionId];
      if (savedSection && savedSection.text) {
        text = savedSection.text;
      }
    }
    return text || '';
  };

  getNotes = () => {
    let text;
    if (this.props.question.notes && this.props.savedSections !== undefined) {
      const savedQuestion = this.props.savedSections[this.props.sectionId]?.questions[this.props.questionId];
      if (savedQuestion && savedQuestion.notes) {
        text = savedQuestion.notes;
      }
    }
    return text || '';
  };

  onNotesChange = (event: React.FormEvent<HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (value !== this.state.notes) {
      this.setState({ notes: value }, () => {
        this.saveOrClearNotes();
      });
    }
  };

  readonly state: State = {
    textAnswer: this.getTextAnswer(),
    textAnswerFocused: false,
    notes: this.getNotes(),
  };

  render() {
    const { question, locale, showError, savedSections, sectionId, questionId } = this.props;
    const { type } = question;
    const { notes, textAnswer, textAnswerFocused } = this.state;

    const otherIsSelected = this.otherIsSelected();
    const savedSection = savedSections ? savedSections[sectionId] : undefined;
    const notesEnabled = question.optional || (savedSection?.questions[questionId] != null && savedSection?.questions[questionId].options.length !== 0);
    const isMultipleAnswersType =
      type === QUESTION_TYPES.MULTIPLE_CHOICES || type === QUESTION_TYPES.MULTIPLE_CHOICES_MULTI_SELECT || type === QUESTION_TYPES.MULTIPLE_CHOICES_WITH_OTHER;

    if (question.hidden) {
      return <></>;
    }

    return (
      <fieldset className="question" id={`question-${question._id}`}>
        <div className="question-number">
          <div className="question-order">
            <span>{question.order}</span>
          </div>

          {showError && <FormattedMessage id="Comparison.Questionnaire.QuestionRequired" />}
        </div>

        <legend className="mb-4">
          <div className="question-title">
            <div
              className="question-title-h1"
              id={`label-${question._id}`}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatLocalizedString(question.title, locale)) }}
            />
          </div>

          {question.description && (
            <>
              {question.description.text && (
                <div className="question-description">
                  <span>{formatLocalizedString(question.description.text, locale)}</span>
                </div>
              )}

              {question.description.imgUrl && (
                <div className="question-description-image">
                  <img src={formatLocalizedString(question.description.imgUrl, locale)} alt="" />
                </div>
              )}
            </>
          )}
        </legend>

        <div className="question-answers" role={isMultipleAnswersType ? 'radiogroup' : undefined} aria-labelledby={`label-${question._id}`}>
          {question.options.length !== 0 && this.renderOptions(question.options, type)}
          {type === QUESTION_TYPES.MULTIPLE_CHOICES_WITH_OTHER &&
            (textAnswerFocused || otherIsSelected ? (
              <textarea
                className="questionnaire-short-answer"
                placeholder={formatMessage('Comparison.Questionnaire.EnterResponse')}
                value={textAnswer}
                onChange={this.onTextAnswerChange}
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                data-private
                data-dd-privacy="mask"
              />
            ) : (
              <SelectableButton className={classNames('answer-button', 'other-button')} onClick={this.onFocus}>
                <span>{formatMessage('Comparison.Questionnaire.Other')}</span>
              </SelectableButton>
            ))}

          {question.notes && (
            <>
              <div className="question-notes-title">{formatMessage('Comparison.Questionnaire.Notes.Title')}</div>
              <textarea
                className="questionnaire-long-answer"
                placeholder={formatMessage('Comparison.Questionnaire.Notes.EnterNotes')}
                value={notes}
                onChange={this.onNotesChange}
                onBlur={this.onNotesBlur}
                data-private
                data-dd-privacy="mask"
                disabled={!notesEnabled}
              />
            </>
          )}

          {type === QUESTION_TYPES.TEXT_ANSWER && (
            <textarea
              className="questionnaire-long-answer"
              placeholder={formatMessage('Comparison.Questionnaire.EnterResponse')}
              value={textAnswer}
              onChange={this.onTextAnswerChange}
              onBlur={this.onBlur}
              data-private
              data-dd-privacy="mask"
            />
          )}
          {type === QUESTION_TYPES.DATE && (
            <DatePicker
              key={`${sectionId}-${questionId}`}
              calendarType="US"
              customFormat="MMMM DD, YYYY"
              compact
              allowPrevNext2
              datePickerClassName="is-in-modal"
              onDateChange={(date: Date) => this.onDateChange(date)}
              defaultDate={textAnswer ? new Date(`${textAnswer}T00:00:00`) : null}
              customPlaceholder={formatMessage('Comparison.Questionnaire.SelectDate')}
            />
          )}
        </div>
      </fieldset>
    );
  }
}

export default Question;
